import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DecisionFooter from '../../DecisionFooter.jsx';
import { DecisionConfirmModal } from '../shared/index.js';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { hitlActionErrorToast } from '../../../utils/hitlActionToast.js';
import { isDecisionSkipped } from '../../../utils/decisionSkipped.js';
import {
  questionMarkedReadToast,
  threadClosedToast,
} from '../../../utils/threadActionToast.js';
import { isDirectQuestionAwaitingAnswer } from '../../../utils/questionDecisionUtils.js';
import { Check, MessageSquareOff, Reply, Send, XCircle } from 'lucide-react';

/**
 * Question decision — prototype thread-centric footer (no inline composer).
 * Reply (Ask modal → QUESTION card in thread) on all open questions.
 * Direct ask also gets Send response (modal → HITL resolve).
 */
function QuestionDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const {
    completeQuestion,
    markQuestionRead,
    closeEventThread,
    getThread,
    pendingDecisionIds,
  } = useWorkQueueContext();
  const [declineOpen, setDeclineOpen] = useState(false);
  const [sendResponseOpen, setSendResponseOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const isResolving = pendingDecisionIds?.has(event.id) ?? false;
  const isThreadClosed = getThread?.(event.id)?.status === 'closed';
  const showSendResponse = isDirectQuestionAwaitingAnswer(event);

  const handleReply = useCallback(() => {
    actions.ask?.(event);
  }, [actions, event]);

  const handleOpenDecline = useCallback(() => setDeclineOpen(true), []);
  const handleCloseDecline = useCallback(() => setDeclineOpen(false), []);
  const handleOpenSendResponse = useCallback(() => setSendResponseOpen(true), []);
  const handleCloseSendResponse = useCallback(() => setSendResponseOpen(false), []);

  const handleMarkRead = useCallback(async () => {
    try {
      await markQuestionRead?.(event.id);
      toast.success(questionMarkedReadToast(t));
    } catch (err) {
      toast.error({
        title: t('thread.postErrorTitle'),
        message: err?.message ?? t('thread.postErrorMessage'),
      });
    }
  }, [event.id, markQuestionRead, t, toast]);

  const handleCloseThread = useCallback(async () => {
    if (closing || isThreadClosed) return;
    setClosing(true);
    try {
      await closeEventThread?.(event.id);
      toast.success(threadClosedToast(t));
    } catch (err) {
      toast.error({
        title: t('thread.postErrorTitle'),
        message: err?.message ?? t('thread.postErrorMessage'),
      });
    } finally {
      setClosing(false);
    }
  }, [closeEventThread, closing, event.id, isThreadClosed, t, toast]);

  const handleSendResponse = useCallback(
    async ({ reason: response }) => {
      const text = response?.trim();
      if (!text || isResolving) return;
      try {
        await completeQuestion(event.id, { outcome: 'answered', response: text });
        toast.success({
          title: t('question.answeredTitle'),
          message: t('question.answeredMessage'),
        });
      } catch (err) {
        if (isDecisionSkipped(err)) return;
        const { title, message } = hitlActionErrorToast(t, err, 'question');
        toast.error({ title, message });
        throw err;
      }
    },
    [completeQuestion, event.id, isResolving, t, toast]
  );

  const handleDecline = useCallback(
    async ({ reason }) => {
      try {
        await completeQuestion(event.id, { outcome: 'declined', reason });
        toast.success(threadClosedToast(t));
      } catch (err) {
        if (isDecisionSkipped(err)) return;
        const { title, message } = hitlActionErrorToast(t, err, 'question');
        toast.error({ title, message });
        throw err;
      }
    },
    [completeQuestion, event.id, t, toast]
  );

  const primaryActions = useMemo(() => {
    if (isThreadClosed) return [];

    const actionsList = [
      {
        id: 'reply',
        label: t('question.reply'),
        size: 'md',
        icon: Reply,
        disabled: isResolving,
        onClick: handleReply,
      },
    ];

    if (showSendResponse) {
      actionsList.push({
        id: 'send-response',
        label: t('question.sendResponse'),
        size: 'md',
        icon: Send,
        variant: 'secondary',
        disabled: isResolving,
        onClick: handleOpenSendResponse,
      });
    }

    actionsList.push(
      {
        id: 'mark-read',
        label: t('question.markAsRead'),
        size: 'md',
        icon: Check,
        variant: 'secondary',
        disabled: isResolving || event.status === 'read',
        onClick: handleMarkRead,
      },
      {
        id: 'decline',
        label: t('question.decline'),
        size: 'md',
        icon: XCircle,
        variant: 'secondary',
        disabled: isResolving,
        onClick: handleOpenDecline,
      },
      {
        id: 'close-thread',
        label: t('question.closeThread'),
        size: 'md',
        icon: MessageSquareOff,
        variant: 'secondary',
        disabled: isResolving || closing,
        onClick: handleCloseThread,
      },
    );

    return actionsList;
  }, [
    closing,
    event.status,
    handleCloseThread,
    handleMarkRead,
    handleOpenDecline,
    handleOpenSendResponse,
    handleReply,
    isResolving,
    isThreadClosed,
    showSendResponse,
    t,
  ]);

  if (isThreadClosed) {
    return null;
  }

  return (
    <>
      <DecisionFooter
        event={event}
        primaryActions={primaryActions}
        onSkip={actions.skip}
        onAsk={actions.ask}
        onEscalate={actions.escalate}
        onTrace={actions.trace}
        extraMenuItems={actions.extraMenuItems}
        sticky={actions.sticky}
        hideTrace
      />

      <DecisionConfirmModal
        isOpen={sendResponseOpen}
        onClose={handleCloseSendResponse}
        icon={<Send size={20} className="text-[#2173FF] dark:text-blue-400" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('question.sendResponseTitle')}
        subtitle={t('question.sendResponseSubtitle')}
        requireReason
        reasonLabel={t('question.yourResponse')}
        reasonPlaceholder={t('question.responsePlaceholder')}
        confirmLabel={t('question.sendResponse')}
        onConfirm={handleSendResponse}
      />

      <DecisionConfirmModal
        isOpen={declineOpen}
        onClose={handleCloseDecline}
        icon={<XCircle size={20} className="text-rose-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('question.declineTitle')}
        subtitle={t('question.declineSubtitle')}
        requireReason
        reasonLabel={t('question.declineReasonLabel')}
        reasonPlaceholder={t('question.declineReasonPlaceholder')}
        confirmLabel={t('question.decline')}
        confirmVariant="danger"
        onConfirm={handleDecline}
      />
    </>
  );
}

export default QuestionDecision;
